import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { ErrorView } from '../../../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../../../common/views/LoadingView';
import type { CatalogItem, GetReleaseKitResponse } from '../../../../../../redux/api/fymAPI.types';
import { useIsDemoAccount } from '../../../../../accountSelector/useIsDemoAccount';
import { useIsReadOnlyAccount } from '../../../../../accountSelector/useIsReadOnlyAccount';
import { Panel } from '../../../../components/primitives/Panel';
import type { ReleasePlanChannels } from '../useReleasePlanChannels';
import { ClosedReelsPlanLine } from './ClosedReelsPlanLine';
import { reelsPlanMessages } from './reelsPlanMessages';
import { reelsPlanPanelState } from './reelsPlanModel';
import { ReelsPlanRunSheet } from './ReelsPlanRunSheet';
import { ReelsPlanSetup } from './ReelsPlanSetup';
import { useReelsPlanAnswer } from './useReelsPlanAnswer';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the AI reels plan; each part uses its own slice of it */
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelsPlanPanelProps {
  accountId: number;
  item: CatalogItem;
  release: GetReleaseKitResponse;
  /** The usable channels of the artist, as the Auto campaign tab read them. */
  channels: ReleasePlanChannels;
}

/**
 * The auto campaign of a song ("X days of AI reels"; design rollout-plan f1-f6, spec 2026-10-12):
 * the whole content of the Auto campaign tab (`campaign/CampaignTab`). The code keeps the first name
 * of the feature, "reels plan". With no campaign it is the setup. With an active campaign it is the
 * run sheet. After the campaign ended, or after its last day, it is the closed line above a new
 * setup. The demo artist and a read-only artist see it with the controls off. While the channels of
 * the tab load or fail, the tab says so and the panel waits.
 */
const ReelsPlanPanelComponent: React.FC<ReelsPlanPanelProps> = ({ accountId, item, release, channels }) => {
  const intl = useIntl();
  const isDemo = useIsDemoAccount();
  const isReadOnly = useIsReadOnlyAccount();
  const canAct = !isDemo && !isReadOnly;
  const isWaiting = channels.isLoading || channels.error !== undefined;
  const { plan, isLoading, error, retry, today } = useReelsPlanAnswer({
    accountId,
    catalogItemUuid: item.uuid,
    platforms: channels.platforms,
    skip: isWaiting,
  });

  if (isWaiting) {
    return null;
  }
  if (isLoading || plan === undefined) {
    return (
      <Panel as="section" aria-label={intl.formatMessage(reelsPlanMessages.title)} pad>
        {isLoading ? <LoadingView /> : <ErrorView error={error} retry={retry} />}
      </Panel>
    );
  }

  const reelsPlan = plan.reelsPlan ?? null;
  const state = reelsPlanPanelState(reelsPlan, today);
  if (state === 'running' && reelsPlan !== null) {
    return (
      <ReelsPlanRunSheet
        accountId={accountId}
        item={item}
        release={release}
        plan={plan}
        reelsPlan={reelsPlan}
        channels={channels.channels}
        today={today}
        canAct={canAct}
      />
    );
  }
  return (
    <div className={styles.wrap}>
      {reelsPlan !== null && <ClosedReelsPlanLine catalogItemUuid={item.uuid} reelsPlan={reelsPlan} />}
      <ReelsPlanSetup
        accountId={accountId}
        catalogItemUuid={item.uuid}
        songTitle={item.title}
        platforms={channels.platforms}
        channels={channels.channels}
        today={today}
        canAct={canAct}
        isNext={reelsPlan !== null}
      />
    </div>
  );
};

export const ReelsPlanPanel = TypedMemo(ReelsPlanPanelComponent);
